import { Directive, ElementRef, HostListener, inject, input, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
    selector: 'input[type=text][stbyNumericInput]',
})
export class NumericInputDirective implements OnInit {
    private el = inject<ElementRef<HTMLInputElement>>(ElementRef);
    private ngControl = inject(NgControl, { optional: true });

    readonly allowNegative = input<boolean>(true);
    readonly integerOnly = input<boolean>(false);
    readonly min = input<number | null>(null);
    readonly max = input<number | null>(null);

    ngOnInit(): void {
        const inputEl = this.el.nativeElement;
        inputEl.inputMode = this.integerOnly() ? 'numeric' : 'decimal';

        const min = this.min();
        if (min !== null) {
            inputEl.setAttribute('min', min.toString());
        }
        const max = this.max();
        if (max !== null) {
            inputEl.setAttribute('max', max.toString());
        }
    }

    @HostListener('input', ['$event.target.value'])
    onInput(value: string): void {
        // Allow standalone minus sign during input
        if (value === '-' && this.allowNegative()) {
            return;
        }

        const sanitized = this.sanitizeValue(value);

        if (sanitized !== value) {
            this.updateValue(sanitized);
        }
    }

    @HostListener('blur')
    onBlur(): void {
        const value = this.el.nativeElement.value;

        // Handle empty input or standalone minus
        if (value === '') {
            this.updateValue('');
            return;
        }

        // Special handling for standalone minus sign - keep it if focus is still in the field
        if (value === '-') {
            if (document.activeElement === this.el.nativeElement) {
                return;
            } else {
                this.updateValue('');
                return;
            }
        }

        let numericValue = this.parseValue(value);

        if (this.integerOnly()) {
            numericValue = Math.round(numericValue);
        }

        const min = this.min();
        if (min !== null && numericValue < min) {
            numericValue = min;
        }
        const max = this.max();
        if (max !== null && numericValue > max) {
            numericValue = max;
        }

        const formattedValue = this.formatValue(numericValue);
        this.updateValue(formattedValue);
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        // Allow navigation keys, backspace, delete, tab
        const navigationKeys = [
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Backspace',
            'Delete',
            'Tab',
            'Home',
            'End',
            'Enter',
        ];

        const allowedCtrlKeys = ['a', 'c', 'v', 'x', 'z'];

        // Allow navigation and editing keys
        if (navigationKeys.includes(event.key)) {
            return;
        }

        // Allow Ctrl/Cmd + common shortcuts
        const isCtrlOrCmd = event.ctrlKey || event.metaKey;
        if (isCtrlOrCmd && allowedCtrlKeys.includes(event.key.toLowerCase())) {
            return;
        }

        // Allow minus sign if negatives are allowed
        if (event.key === '-' && this.allowNegative()) {
            const inputEl = this.el.nativeElement;
            const cursorPos = inputEl.selectionStart || 0;
            const selectionLength = (inputEl.selectionEnd || 0) - cursorPos;

            if (cursorPos === 0) {
                if (!inputEl.value.includes('-') || (inputEl.value.startsWith('-') && selectionLength > 0)) {
                    return;
                }
            }
            event.preventDefault();
            return;
        }

        // Decimal separators handling
        if (event.key === '.' || event.key === ',') {
            // Prevent decimal separators in integer-only mode
            if (this.integerOnly()) {
                event.preventDefault();
                return;
            }

            const inputEl = this.el.nativeElement;
            // Check if the input already contains either a period or comma
            if (inputEl.value.includes('.') || inputEl.value.includes(',')) {
                event.preventDefault(); // Prevent multiple decimal separators
            }
            return;
        }

        // Allow numbers (0-9)
        if (/^\d$/.test(event.key)) {
            return;
        }

        event.preventDefault();
    }

    private sanitizeValue(value: string): string {
        // Handle standalone minus sign
        const allowNegative = this.allowNegative();
        if (value === '-' && allowNegative) {
            return '-';
        }

        let isNegative = false;
        let sanitized = value;

        if (allowNegative && value.startsWith('-')) {
            isNegative = true;
            sanitized = value.substring(1);
        }

        if (this.integerOnly()) {
            // Remove anything that's not a digit
            sanitized = sanitized.replace(/[^0-9]/g, '');
        } else {
            // Remove anything that's not a digit, comma, or period
            sanitized = sanitized.replace(/[^0-9.,]/g, '');

            let hasSeparator = false;
            let result = '';

            for (let i = 0; i < sanitized.length; i++) {
                const char = sanitized[i];
                if ((char === '.' || char === ',') && !hasSeparator) {
                    result += char; // Keep the first separator as is
                    hasSeparator = true;
                } else if (char !== '.' && char !== ',') {
                    result += char; // Keep all non-separator characters
                }
            }

            sanitized = result;
        }

        // Re-add negative sign if needed
        if (isNegative && allowNegative) {
            sanitized = '-' + sanitized;
        }

        return sanitized;
    }

    private parseValue(value: string): number {
        return parseFloat(value.replace(/,/g, '.'));
    }

    private formatValue(value: number): string {
        if (isNaN(value)) return '';

        if (this.integerOnly()) {
            return Math.round(value).toString();
        } else {
            const originalValue = this.el.nativeElement.value;
            const separator = originalValue.includes(',') ? ',' : '.';

            return value.toString().replace('.', separator);
        }
    }

    private updateValue(value: string): void {
        this.el.nativeElement.value = value;

        if (this.ngControl && this.ngControl.control) {
            const parsedValue = value === '' || value === '-' ? null : this.parseValue(value);
            this.ngControl.control.setValue(parsedValue, { emitEvent: true });
        }

        const event = new Event('input', { bubbles: true });
        this.el.nativeElement.dispatchEvent(event);
    }
}
